import { FormProvider } from "@components/hook-form";
import { Drawer, useTheme } from "@mui/material";
import { RootState, useSelector } from "@redux/store";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useChatHistory } from "../../hooks/useChatHistory";
import { PendingUpload } from "../../hooks/useFileUpload";
import { ISendChatMessage } from "../../types";
import { AskDostContent } from "../AskDostContent";
import { AskDostHeader } from "../AskDostHeader";

// Internal
import { mapConversationHistoryToChatMessages } from "../../utils/utils";
import { useAskDostStyles } from "../../styles/askDostStyles";
import { resumeConversation } from "../../services/askDostService";

interface IAskDostProps {
  isOpen?: boolean;
  onClose?: () => void;
  translate: (text: string, options?: any) => string;
  initialData?: ISendChatMessage;
  initialPendingUploads?: PendingUpload[];
}

export const AskDostDrawer = ({
  isOpen = true,
  onClose,
  translate,
  initialData,
  initialPendingUploads,
}: IAskDostProps) => {
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { askDostDrawer: askDostDrawerStyles } = useAskDostStyles({
    isFullWidth,
  });
  const { palette } = useTheme();
  const pendingUploadsRef = useRef<PendingUpload[]>(
    initialPendingUploads ?? [],
  );

  const userInfo = useSelector(
    (state: RootState) => state.credential?.userCred,
  );
  const chatSettings = useSelector((state: RootState) => state.askDostChat);

  const methods = useForm<ISendChatMessage>({
    defaultValues: {
      message: initialData?.message ?? "",
      attachments: initialData?.attachments ?? [],
      thinking: chatSettings?.thinking || false,
      level: chatSettings?.level || "recommended",
    },
  });
  const { handleSubmit, watch, setValue, reset } = methods;

  const data = watch();

  const chatHistory = useChatHistory({ translate, reset });

  const { clearHistory, setActiveConversationWithMessages, isResetting } =
    chatHistory;

  const handleExpandDrawer = () => {
    setIsFullWidth(!isFullWidth);
  };

  const onSubmit = async () => {
    setIsSending(false);
  };

  const handleSelectPastConversation = async (
    conversationId: string,
    conv?: any,
  ) => {
    const displayTitle =
      conv?.title || conv?.name || translate("chat_n", { id: conversationId });

    try {
      const response = await resumeConversation(conversationId);
      const rawMessages = response?.data?.messages || response?.data || [];

      const processedMessages = rawMessages.map((msg: any) => ({
        ...msg,
        attachments: msg.attachments || msg.files || [],
      }));

      setActiveConversationWithMessages(
        conversationId,
        displayTitle,
        mapConversationHistoryToChatMessages(processedMessages, translate),
      );
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  return (
    <FormProvider methods={methods}>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        hideBackdrop
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        sx={askDostDrawerStyles}
      >
        <AskDostHeader
          isFullWidth={isFullWidth}
          onExpandClick={handleExpandDrawer}
          palette={palette}
          translate={translate}
          isResetting={isResetting}
          clearHistory={clearHistory}
          getNavigationState={() => ({
            data,
            pendingUploads: pendingUploadsRef.current,
          })}
          onSelectConversation={handleSelectPastConversation}
          onClose={onClose}
        />
        <AskDostContent
          data={data}
          onMessageChange={(value) => setValue("message", value)}
          onAttachmentsChange={(attachments) =>
            setValue("attachments", attachments)
          }
          onSubmit={handleSubmit(onSubmit)}
          isSending={isSending}
          userInfo={userInfo}
          palette={palette}
          translate={translate}
          chatHistory={chatHistory}
          initialPendingUploads={pendingUploadsRef.current}
          onPendingUploadsChange={(pendingUploads) => {
            pendingUploadsRef.current = pendingUploads;
          }}
        />
      </Drawer>
    </FormProvider>
  );
};